/** @fileoverview Composes assistant chat, live preview, and app builder controls. */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction, CSSProperties, KeyboardEvent, PointerEvent } from 'react';
import { getCurrentBuilderUrl, getHostedAppUrl } from '../../appMode/hostConfig';
import { getFabrikApp, renameFabrikApp, switchFabrikAppSystem } from '../../services/appsApi';
import useConnectedSystems from '../../hooks/useConnectedSystems';
import type { FabrikAppDefinition, FabrikAppSummary } from '../../types/apps';
import type { Tenant, User } from '../../types/identity';
import AppBuilderHeader from './AppBuilderHeader';
import LivePreviewPane from './LivePreviewPane';
import usePreviewDraftBridge from './usePreviewDraftBridge';
import PlanWorkspace from './PlanWorkspace';
import { getPlan, getPlanAtRevision } from '../../services/planApi';

type Props = {
  tenant: Tenant;
  user?: User;
  appId: string;
  setChatPortalTarget: Dispatch<SetStateAction<HTMLElement | null>>;
  setPortalChatOptions: Dispatch<
    SetStateAction<{
      previewOpen: boolean;
      onPreviewToggle: () => void;
      onAppDraftUpdated: (definition: FabrikAppDefinition) => void;
    } | null>
  >;
  onBack: () => void;
};

const ASSISTANT_MIN_PX = 480;
const ASSISTANT_MAX_PX = 720;
const PREVIEW_MIN_PX = 400;

/**
 * Clamps a sidebar width so the chat and the preview both keep a usable size.
 * @param percent - requested width as a percent of the layout
 * @param layoutWidth - layout width in pixels
 */
const clampAssistantWidth = (percent: number, layoutWidth: number) => {
  const minPercent = (ASSISTANT_MIN_PX / layoutWidth) * 100;
  const maxPercent = (Math.min(ASSISTANT_MAX_PX, layoutWidth - PREVIEW_MIN_PX) / layoutWidth) * 100;
  return Math.max(minPercent, Math.min(Math.max(minPercent, maxPercent), percent));
};

export default function AppBuilderWorkspace({
  tenant,
  user,
  appId,
  setChatPortalTarget,
  setPortalChatOptions,
  onBack,
}: Props) {
  const tenantId = tenant.tenant_id;
  const systems = useConnectedSystems(tenantId);
  const previewUrl = `/t/${encodeURIComponent(tenantId)}/apps/${encodeURIComponent(appId)}/preview`;
  const builderUrl = getCurrentBuilderUrl() || window.location.origin;
  const draftPreviewUrl = `${builderUrl.replace(/\/+$/, '')}/${previewUrl.replace(/^\/+/, '')}`;
  const [previewOpen, setPreviewOpen] = useState(true);
  const [paneView, setPaneView] = useState<'preview' | 'settings'>('preview');
  const [planAvailable, setPlanAvailable] = useState(false);
  const [appDraftRevision, setAppDraftRevision] = useState(0);
  const [assistantWidth, setAssistantWidth] = useState(25);
  const [resizingAssistant, setResizingAssistant] = useState(false);
  // Too narrow for both panes: show one at a time instead of squeezing them side by side.
  const [stacked, setStacked] = useState(false);
  const stackedRef = useRef(false);
  const [app, setApp] = useState<FabrikAppSummary>();
  const previewLayoutRef = useRef<HTMLDivElement>(null);
  const { previewFrameRef, onAppDraftUpdated: updatePreviewDraft } = usePreviewDraftBridge(appId);

  const onAppDraftUpdated = useCallback(
    (definition: FabrikAppDefinition) => {
      window.dispatchEvent(new CustomEvent('fabrik:preview-selection-clear'));
      setAppDraftRevision((revision) => revision + 1);
      updatePreviewDraft(definition);
      getFabrikApp(tenantId, appId)
        .then(setApp)
        .catch(() => undefined);
    },
    [appId, tenantId, updatePreviewDraft]
  );
  const onPaneViewChange = useCallback((view: 'preview' | 'settings') => setPaneView(view), []);
  const onPreviewToggle = useCallback(() => setPreviewOpen((open) => !open), []);
  const onPlanWorkflow = useCallback((available: boolean) => {
    setPlanAvailable(available);
  }, []);
  const onRenameApp = useCallback(
    async (name: string, preserveOldUrl: boolean) => {
      const updated = await renameFabrikApp(tenantId, appId, name, preserveOldUrl);
      setApp(updated);
    },
    [appId, tenantId]
  );
  const onSystemChange = useCallback(
    async (version: 'draft' | 'published', sourceSystemId: string, targetSystemId: string) => {
      const updated = await switchFabrikAppSystem(
        tenantId,
        appId,
        sourceSystemId,
        targetSystemId,
        version
      );
      if (version === 'draft') {
        window.dispatchEvent(new CustomEvent('fabrik:preview-selection-clear'));
      }
      setApp(updated);
    },
    [appId, tenantId]
  );

  useEffect(() => {
    setPlanAvailable(false);
    let controller = new AbortController();
    const refreshPlan = (minimumRevision?: number) => {
      controller.abort();
      const requestController = new AbortController();
      controller = requestController;
      const request =
        minimumRevision === undefined
          ? getPlan(tenantId, appId, requestController.signal)
          : getPlanAtRevision(tenantId, appId, minimumRevision, requestController.signal);
      request
        .then((plan) => {
          if (requestController.signal.aborted) return;
          if (minimumRevision !== undefined && (plan.revision ?? 0) < minimumRevision) return;
          setPlanAvailable(plan.plan !== null);
        })
        .catch(() => undefined);
    };
    const onPlanUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ appId?: unknown; revision?: unknown }>).detail;
      if (detail?.appId === appId && typeof detail.revision === 'number') {
        refreshPlan(detail.revision);
      }
    };
    refreshPlan();
    window.addEventListener('fabrik:app-plan-updated', onPlanUpdated);
    return () => {
      controller.abort();
      window.removeEventListener('fabrik:app-plan-updated', onPlanUpdated);
    };
  }, [appId, tenantId]);

  useEffect(() => {
    setPortalChatOptions({
      previewOpen,
      onPreviewToggle,
      onAppDraftUpdated,
    });
    return () => setPortalChatOptions(null);
  }, [onAppDraftUpdated, onPreviewToggle, previewOpen, setPortalChatOptions]);

  useEffect(() => {
    const controller = new AbortController();
    getFabrikApp(tenantId, appId, controller.signal)
      .then(setApp)
      .catch(() => undefined);
    return () => controller.abort();
  }, [appId, tenantId]);

  // Re-applies the width limits when the layout size changes (window resize, first render).
  // Limits live here, not in CSS, because the grid column transition cannot animate clamp().
  useEffect(() => {
    const layout = previewLayoutRef.current;
    if (!layout) return;
    const observer = new ResizeObserver(([entry]) => {
      const layoutWidth = entry.contentRect.width;
      if (!layoutWidth) return;
      setAssistantWidth((width) => clampAssistantWidth(width, layoutWidth));
      const nextStacked = layoutWidth < ASSISTANT_MIN_PX + PREVIEW_MIN_PX;
      if (nextStacked === stackedRef.current) return;
      stackedRef.current = nextStacked;
      setStacked(nextStacked);
      // Close the preview on entering the narrow layout so the chat stays visible.
      if (nextStacked) setPreviewOpen(false);
    });
    observer.observe(layout);
    return () => observer.disconnect();
  }, []);

  const sideBySide = previewOpen && !stacked;
  let gridColumns = 'minmax(0, 1fr) minmax(0, 0fr)';
  let gridRows = 'minmax(0, 1fr)';
  if (sideBySide) gridColumns = `${assistantWidth}% minmax(0, 1fr)`;
  else if (previewOpen && stacked) {
    gridColumns = 'minmax(0, 1fr)';
    gridRows = 'minmax(0, 1fr) minmax(0, 1fr)';
  } else if (previewOpen) gridColumns = 'minmax(0, 0fr) minmax(0, 1fr)';

  const publishedPreviewUrl = app?.publishedVersion
    ? getHostedAppUrl({ slug: app.slug, preview: false })
    : '';

  const resizeAssistant = useCallback((clientX: number) => {
    const layout = previewLayoutRef.current;
    if (!layout) return;
    const { left, width } = layout.getBoundingClientRect();
    setAssistantWidth(clampAssistantWidth(((clientX - left) / width) * 100, width));
  }, []);

  const handleResizePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      setResizingAssistant(true);
      event.currentTarget.setPointerCapture(event.pointerId);
      resizeAssistant(event.clientX);
    },
    [resizeAssistant]
  );

  const handleResizePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) resizeAssistant(event.clientX);
    },
    [resizeAssistant]
  );

  const handleResizePointerEnd = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setResizingAssistant(false);
  }, []);

  const handleResizeKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const layoutWidth = previewLayoutRef.current?.getBoundingClientRect().width;
      if (!layoutWidth) return;
      let nextWidth: number | undefined;
      if (event.key === 'ArrowLeft') nextWidth = assistantWidth - 2;
      if (event.key === 'ArrowRight') nextWidth = assistantWidth + 2;
      if (event.key === 'Home') nextWidth = 0;
      if (event.key === 'End') nextWidth = 100;
      if (nextWidth === undefined) return;
      event.preventDefault();
      setAssistantWidth(clampAssistantWidth(nextWidth, layoutWidth));
    },
    [assistantWidth]
  );

  return (
    <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-app-surface-muted">
      <AppBuilderHeader
        appName={app?.name || 'App'}
        app={app}
        previewOpen={previewOpen}
        user={user}
        tenant={tenant}
        onBack={onBack}
        onAppChange={setApp}
        onTogglePreview={onPreviewToggle}
        systems={systems}
      />
      <div
        ref={previewLayoutRef}
        className={`relative grid fabrik-builder-grid min-h-0 min-w-0 flex-1 ease-in-out motion-reduce:transition-none ${
          resizingAssistant ? '' : 'transition-grid-template duration-300'
        }`}
        style={
          {
            '--fabrik-assistant-width': `${assistantWidth}%`,
            '--fabrik-builder-grid-columns': gridColumns,
            '--fabrik-builder-grid-rows': gridRows,
          } as CSSProperties
        }
      >
        <div
          id="fabrik-assistant-sidebar"
          className={`flex min-h-0 min-w-0 flex-col overflow-hidden bg-app-surface-muted ${
            sideBySide ? 'py-3 pl-3 sm:py-5 sm:pl-5' : ''
          }`}
        >
          {/* Framed as a card only beside the preview card; full width it sits on the page. Kept mounted either way so chat state survives the toggle. */}
          <div
            ref={setChatPortalTarget}
            className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${
              sideBySide ? 'rounded-xl border border-app-border shadow-sm' : ''
            }`}
          />
        </div>
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden bg-app-surface-muted">
          <LivePreviewPane
            appId={appId}
            appName={app?.name || 'App'}
            appSlug={app?.slug || ''}
            onRenameApp={onRenameApp}
            onDefinitionChange={(definition) => {
              setApp((current) =>
                current ? { ...current, draft_definition: definition } : current
              );
            }}
            previewUrl={previewUrl}
            draftPreviewUrl={draftPreviewUrl}
            publishedPreviewUrl={publishedPreviewUrl}
            publishedVersionNo={app?.publishedVersion?.versionNo}
            previewOpen={previewOpen}
            previewFrameRef={previewFrameRef}
            view={paneView}
            onViewChange={onPaneViewChange}
            tenantId={tenantId}
            tenantName={tenant.name}
            appDefinition={app?.draft_definition}
            publishedDefinition={app?.publishedVersion?.definition}
            systems={systems}
            onSystemChange={onSystemChange}
            planAvailable={planAvailable}
            appDraftRevision={appDraftRevision}
            planWorkspace={
              planAvailable ? (
                <PlanWorkspace
                  appId={appId}
                  tenantId={tenantId}
                  appName={app?.name || 'App'}
                  onWorkflow={onPlanWorkflow}
                />
              ) : undefined
            }
          />
        </div>
        <div
          role={sideBySide ? 'separator' : undefined}
          aria-label={sideBySide ? 'Resize assistant sidebar' : undefined}
          aria-orientation="vertical"
          aria-controls="fabrik-assistant-sidebar"
          aria-valuenow={Math.round(assistantWidth)}
          aria-hidden={!sideBySide}
          tabIndex={sideBySide ? 0 : -1}
          className={`group absolute inset-y-0 left-(--fabrik-assistant-width) z-10 flex w-3 touch-none items-center justify-center focus-visible:outline-none sm:w-5 ${
            sideBySide ? 'cursor-col-resize' : 'pointer-events-none opacity-0'
          }`}
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerEnd}
          onPointerCancel={handleResizePointerEnd}
          onLostPointerCapture={handleResizePointerEnd}
          onKeyDown={handleResizeKeyDown}
        >
          {/* The handle fills the gap before the preview card: a soft guide line on hover, plus a grip pill that is always visible. */}
          <span
            className={`absolute inset-y-3 left-1/2 w-px -translate-x-1/2 transition-colors sm:inset-y-5 ${
              resizingAssistant
                ? 'bg-app-brand-strong'
                : 'bg-transparent group-hover:bg-app-border group-focus-visible:bg-app-brand-strong'
            }`}
          />
          <span
            className={`relative h-10 w-1.5 rounded-full transition-colors ${
              resizingAssistant
                ? 'bg-app-brand-strong'
                : 'bg-app-border group-hover:bg-app-border-strong group-focus-visible:bg-app-brand-strong'
            }`}
          />
        </div>
      </div>
    </main>
  );
}
